import Element from './Element';
import ClickableMixin from './mixins/ClickableMixin';
import ContainableMixin from './mixins/ContainableMixin';
import * as members from '../privates/privateMembers';
import mixin from '../privates/utils/mixin';

/**
 * @class Page
 * @summary A page is a web page that can contain other elements.
 * @classdesc
 *  Some `Page` properties refer only to the central part of the web page as show here:
 *
 * ![Wix Page](images/page.png "Wix Page")
 *
 *  For example, elements placed within this central area are the page's
 *  [children](#children), but elements placed in the page
 *  [header]($w.Header.html) are not.
 *
 *  Other `Page` properties refer to the page in general.
 * 
 *  The `Page` is part of the [`Document`]($w.Document.html). 
 * @see [Header]($w.Header.html)
 * @see [Footer]($w.Footer.html)
 * @see [Document]($w.Document.html)
 * @mixes $w.Element
 * @mixes $w.ClickableMixin
 * @mixes $w.ContainableMixin
 * @memberof $w
 * @queryable
 * @viewername mobile.core.components.Page
 * @definitionId mobile.core.components.Page
 * @tagname page
 */

class Page extends Element {

    /**
     * @member description
     * @label property
     * @syntax
     * get description(): string
     * @summary Gets the page's <a href="https://support.wix.com/en/article/adding-seo-page-titles-and-descriptions-meta-tags" target="_blank">SEO description</a>.
     * @description
     *  The page description is a meta tag. It provides potential site visitors
     *  with a summary of what the page is about. It is not visible to users,
     *  but appears below your site's title in search results.
     *
     *  A page's `description` is <a href="https://support.wix.com/en/article/changing-your-page-description" target="_blank">set in the Editor</a>
     *  using the <a href="https://support.wix.com/en/article/page-settings" target="_blank">Page Settings panel</a>.
     * @snippet [Page-description_get.es6=Get the page's description]
     * @type {external:String}
     * @memberof $w.Page
     * @instance
     * @readonly
     */
    get description() {
        const RMI = members._RMI.get(this);
        const pageData = RMI.getPageData();

        return pageData && pageData.currentPageSEODescription;
    }

    /**
     * @member keywords
     * @label property
     * @syntax
     * get keywords(): string[]
     * @summary Gets the page's keywords.
     * @description
     *  The <a href="https://support.wix.com/en/article/page-keywords" target="_blank">page's keywords</a>
     *  are not used by Google, but may be used by other search engines.
     *
     *  Page `keywords` are set in the Editor using the
     *  <a href="https://support.wix.com/en/article/page-settings" target="_blank">Page Settings panel</a>.
     * @snippet [Page-keywords_get.es6=Get the page's description]
     * @type {external:String[]}
     * @memberof $w.Page
     * @instance
     * @readonly
     */
    get keywords() {
        const RMI = members._RMI.get(this);
        const pageData = RMI.getPageData();

        return pageData && pageData.currentPageSEOKeywords;
    }

    /**
     * @member {external:String} title
     * @label property
     * @syntax
     * get title(): string
     * @summary Gets the page's title.
     * @description
     *  The <a href="https://www.wix.com/support/html5/article/page-title-meta-tag" target="_blank">page title</a> is used in:
     *
     *  + Search results.
     *  + When saving to favorites.
     *  + In the browser's tab.
     *
     *
     *  A page's `title` is <a href="https://support.wix.com/en/article/changing-your-page-title" target="_blank">set in the Editor</a>
     *  using the <a href="https://support.wix.com/en/article/page-settings" target="_blank">Page Settings panel</a>.
     * @note
     *  The `title` in Preview mode is different than the `title` of the published site.
     * @snippet [Page-title_get.es6=Get the page's title]
     * @memberof $w.Page
     * @instance
     * @readonly
     */
    get title() {
        const RMI = members._RMI.get(this);
        const pageData = RMI.getPageData();

        return pageData && pageData.currentUrlPageTitle;
    }

    /**
     * @member visibleInMenu
     * @label property
     * @syntax
     * get visibleInMenu(): boolean
     * @summary Indicates if the page is included in the site's menu.
     * @description
     *  Whether a page is visible in the site menu is <a href="https://www.wix.com/support/html5/article/hiding-a-page-from-the-menu" target="_blank">set in the Editor</a>
     *  using the <a href="https://support.wix.com/en/article/page-settings" target="_blank">Page Settings panel</a>.
     * @snippet [Page-visibleInMenu_get.es6=Get whether the page appears in the site menu]
     * @type {external:Boolean}
     * @memberof $w.Page
     * @instance
     * @readonly
     */
    get visibleInMenu() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const pageData = RMI.getData(compId);
        if (pageData) {
            return !pageData.hidePage;
        }
    }

    toJSON() {
        const {id, type, global, rendered, title, description, url, fullUrl, keywords, visibleInMenu} = this;
        return {id, type, global, rendered, title, description, url, fullUrl, keywords, visibleInMenu};
    }
}

mixin(Page.prototype, ClickableMixin.prototype);
mixin(Page.prototype, ContainableMixin.prototype);

export default Page;
